import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { ArrowLeft, Clock, Wallet, CheckCircle2, AlertTriangle, ArrowRight } from "lucide-react";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { FaqSourceNote } from "@/components/FaqSourceNote";
import type { HowToGuide } from "@/content/howto/registry";
import { HOWTO_META, getHowToMeta } from "@/content/howto/meta.gen";
import { INSIGHT_LINKS as INSIGHTS } from "@/content/insights/links.gen";
import { SERVICES } from "@/lib/services";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import { buildBreadcrumbSchema, buildFaqSchema, buildSpeakableSchema } from "@/lib/schema";

export const Route = createFileRoute("/howto/$slug")({
  component: HowToDetail,
  loader: async ({ params }): Promise<{ guide: HowToGuide }> => {
    const { getHowToBySlug } = await import("@/content/howto/registry");
    const guide = getHowToBySlug(params.slug);
    if (!guide) throw notFound();
    return { guide };
  },
  head: ({ params, loaderData }) => {
    const guide = loaderData?.guide;
    if (!guide) {
      const meta = getHowToMeta(params.slug);
      if (!meta) return {};
      return pageHeadForProvince({
        title: `${meta.metaTitle} | Thai Notary`,
        description: meta.metaDesc,
        path: `/howto/${meta.slug}`,
        type: "article",
      });
    }
    const site = getActiveSiteUrl();
    const base = pageHeadForProvince({
      title: `${guide.metaTitle} | Thai Notary`,
      description: guide.metaDesc,
      path: `/howto/${guide.slug}`,
      type: "article",
    });
    const howToSchema: Record<string, unknown> = {
      "@context": "https://schema.org",
      "@type": "HowTo",
      "@id": `${site}/howto/${guide.slug}#howto`,
      name: guide.title,
      description: guide.metaDesc,
      totalTime: guide.totalTime,
      supply: guide.supplies.map((s) => ({ "@type": "HowToSupply", name: s })),
      tool: guide.tools.map((t) => ({ "@type": "HowToTool", name: t.name })),
      step: guide.steps.map((s, i) => ({
        "@type": "HowToStep",
        position: i + 1,
        name: s.name,
        text: s.text,
        url: `${site}/howto/${guide.slug}#step-${i + 1}`,
      })),
    };
    if (guide.estimatedCost) {
      howToSchema.estimatedCost = {
        "@type": "MonetaryAmount",
        currency: guide.estimatedCost.currency,
        value: `${guide.estimatedCost.min}-${guide.estimatedCost.max}`,
      };
    }
    return {
      ...base,
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "หน้าแรก", url: "/" },
              { name: "How-To", url: "/howto" },
              { name: guide.title, url: `/howto/${guide.slug}` },
            ]),
          ),
        },
        { type: "application/ld+json", children: JSON.stringify(howToSchema) },
        { type: "application/ld+json", children: JSON.stringify(buildFaqSchema(guide.faqs)) },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            "@id": `${site}/howto/${guide.slug}#webpage`,
            url: `${site}/howto/${guide.slug}`,
            name: guide.title,
            description: guide.metaDesc,
            inLanguage: "th-TH",
            speakable: {
              "@type": "SpeakableSpecification",
              cssSelector: ["h1", "#howto-intro"],
            },
          }),
        },
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema(`/howto/${guide.slug}`)) },
      ],
    };
  },
});

function HowToDetail() {
  const { guide } = Route.useLoaderData() as { guide: HowToGuide };
  const related = HOWTO_META.filter(
    (g) => g.slug !== guide.slug && g.category === guide.category,
  ).slice(0, 3);
  const linkedServices = (guide.relatedServices ?? [])
    .map((s) => SERVICES.find((sv) => sv.slug === s))
    .filter(Boolean);
  const linkedInsights = (guide.relatedInsights ?? [])
    .map((s) => INSIGHTS.find((i) => i.slug === s))
    .filter(Boolean);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeader />
      <main className="mx-auto max-w-4xl px-4 py-12">
        <nav aria-label="Breadcrumb" className="mb-4 text-sm text-muted-foreground">
          <Link to="/" className="hover:text-primary">หน้าแรก</Link> /{" "}
          <Link to="/howto" className="hover:text-primary">How-To</Link> /{" "}
          <span>{guide.title}</span>
        </nav>

        <Link
          to="/howto"
          className="mb-6 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-primary"
        >
          <ArrowLeft className="h-4 w-4" /> กลับสู่ How-To ทั้งหมด
        </Link>

        <header className="mb-8">
          <h1 className="text-3xl font-extrabold md:text-4xl">{guide.title}</h1>
          <p id="howto-intro" className="mt-4 text-lg text-muted-foreground">{guide.intro}</p>
          <div className="mt-5 flex flex-wrap gap-4 text-sm">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-3 py-1 text-primary">
              <Clock className="h-4 w-4" /> {guide.totalTimeLabel}
            </span>
            {guide.estimatedCost && (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-gold/10 px-3 py-1 text-foreground">
                <Wallet className="h-4 w-4" />
                {guide.estimatedCost.min.toLocaleString()}–{guide.estimatedCost.max.toLocaleString()} ฿
              </span>
            )}
            <span className="inline-flex items-center gap-1.5 rounded-full bg-muted px-3 py-1">
              {guide.steps.length} ขั้นตอน
            </span>
          </div>
        </header>

        <section
          id="ai-answer-tldr"
          className="mb-10 rounded-2xl border-2 border-primary/20 bg-primary/5 p-6"
        >
          <p className="text-xs font-bold uppercase tracking-widest text-primary">TL;DR</p>
          <p className="mt-2 text-base leading-relaxed">{guide.tldr}</p>
        </section>

        <section className="mb-10 grid gap-6 md:grid-cols-2">
          <div className="rounded-2xl border border-border bg-card p-6">
            <h2 className="text-lg font-bold">เอกสาร/ของที่ต้องเตรียม</h2>
            <ul className="mt-3 space-y-2 text-sm">
              {guide.supplies.map((s) => (
                <li key={s} className="flex gap-2">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 flex-none text-primary" />
                  <span>{s}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-2xl border border-border bg-card p-6">
            <h2 className="text-lg font-bold">หน่วยงาน/บุคคลที่เกี่ยวข้อง</h2>
            <ul className="mt-3 space-y-2 text-sm">
              {guide.tools.map((t) => (
                <li key={t.name}>
                  <div className="font-semibold">{t.name}</div>
                  {t.note && <div className="text-xs text-muted-foreground">{t.note}</div>}
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section className="mb-12">
          <h2 className="mb-6 text-2xl font-bold">ขั้นตอน ({guide.steps.length} ขั้น)</h2>
          <ol className="space-y-6">
            {guide.steps.map((s, i) => (
              <li
                key={i}
                id={`step-${i + 1}`}
                className="relative rounded-2xl border-2 border-border bg-card p-6 pl-16"
              >
                <span className="absolute left-4 top-6 flex h-9 w-9 items-center justify-center rounded-full bg-primary text-base font-bold text-primary-foreground">
                  {i + 1}
                </span>
                <h3 className="text-lg font-bold">{s.name}</h3>
                <p className="mt-2 text-base leading-relaxed">{s.text}</p>
                {s.duration && (
                  <p className="mt-2 inline-flex items-center gap-1 text-xs text-muted-foreground">
                    <Clock className="h-3.5 w-3.5" /> ใช้เวลา: {s.duration}
                  </p>
                )}
                {s.tip && (
                  <div className="mt-3 rounded-lg border border-gold/30 bg-gold/5 p-3 text-sm">
                    <span className="font-bold text-gold">💡 Tip:</span> {s.tip}
                  </div>
                )}
              </li>
            ))}
          </ol>
        </section>

        {guide.pitfalls.length > 0 && (
          <section className="mb-12 rounded-2xl border-2 border-destructive/30 bg-destructive/5 p-6">
            <h2 className="flex items-center gap-2 text-xl font-bold text-destructive">
              <AlertTriangle className="h-5 w-5" /> กับดักที่พบบ่อย
            </h2>
            <ul className="mt-4 space-y-2 text-sm">
              {guide.pitfalls.map((p) => (
                <li key={p} className="flex gap-2">
                  <span className="text-destructive">⚠</span>
                  <span>{p}</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        <section className="mb-12">
          <h2 className="mb-4 text-2xl font-bold">คำถามที่พบบ่อย</h2>
          <div className="space-y-3">
            {guide.faqs.map((f, i) => (
              <details key={i} className="rounded-xl border border-border bg-card p-4">
                <summary className="cursor-pointer font-semibold">{f.q}</summary>
                <p className="mt-2 text-sm text-muted-foreground">{f.a}</p>
              </details>
            ))}
          </div>
        </section>

        {linkedServices.length > 0 && (
          <section className="mb-10">
            <h2 className="mb-4 text-xl font-bold">บริการที่เกี่ยวข้อง</h2>
            <div className="grid gap-3 md:grid-cols-2">
              {linkedServices.map((sv) => (
                <Link
                  key={sv!.slug}
                  to="/services/$service"
                  params={{ service: sv!.slug }}
                  className="rounded-xl border border-border bg-card p-4 hover:border-gold"
                >
                  <div className="font-semibold">{sv!.title}</div>
                  <span className="mt-1 inline-flex items-center gap-1 text-xs text-primary">
                    ดูบริการ <ArrowRight className="h-3 w-3" />
                  </span>
                </Link>
              ))}
            </div>
          </section>
        )}

        {linkedInsights.length > 0 && (
          <section className="mb-10">
            <h2 className="mb-4 text-xl font-bold">บทความที่เกี่ยวข้อง</h2>
            <ul className="space-y-2 text-sm">
              {linkedInsights.map((p) => (
                <li key={p!.slug}>
                  <Link
                    to="/insights/$slug"
                    params={{ slug: p!.slug }}
                    className="text-primary hover:underline"
                  >
                    → {p!.label}
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        )}

        {related.length > 0 && (
          <section className="mb-10">
            <h2 className="mb-4 text-xl font-bold">คู่มืออื่นในหมวดเดียวกัน</h2>
            <div className="grid gap-3 md:grid-cols-3">
              {related.map((g) => (
                <Link
                  key={g.slug}
                  to="/howto/$slug"
                  params={{ slug: g.slug }}
                  className="rounded-xl border border-border bg-card p-4 hover:border-gold"
                >
                  <div className="text-sm font-semibold">{g.title}</div>
                </Link>
              ))}
            </div>
          </section>
        )}
        <div className="mx-auto max-w-4xl px-4 pb-12">
          <FaqSourceNote topic="consular" />
        </div>

      </main>
      <SiteFooter />
    </div>
  );
}